"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "food-storage-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

interface StorageItem {
  item: string
  cubicFeet: number
  tips: string
}

const META = {
  name: "Holiday Food Storage Calculator",
  category: "Kitchen Planning" as const,
  mediumDescription:
    "Plan refrigerator and freezer space for Thanksgiving, Christmas, Easter, and 4th of July gatherings. Uses USDA-recommended portion sizes per guest.",
  faqs: [
    {
      question: "How much turkey should I plan per person?",
      answer:
        "USDA guidance is 1 lb of bone-in turkey per adult guest, 1.5 lb if you want generous leftovers. For our calculation we use 1.25 lb as a balanced default.",
    },
    {
      question: "How long does it take to thaw a frozen turkey?",
      answer:
        "USDA says approximately 24 hours of refrigerator thaw time per 4–5 lbs of turkey. A 16-lb bird needs 3.5–4 days. Never thaw on the counter — bacteria grow above 40°F.",
    },
    {
      question: "How long are leftovers safe?",
      answer:
        "USDA-safe refrigerator life for cooked turkey, ham, and side dishes is 3–4 days at or below 40°F. Freeze anything you won't finish in that window.",
    },
    {
      question: "What about ham for Easter?",
      answer:
        "USDA portion guidance is roughly half a pound of bone-in ham per adult. Spiral-cut hams are pre-sliced so plan slightly less waste than a whole-bone ham.",
    },
    {
      question: "Why does the calculator suggest cleaning the fridge a week ahead?",
      answer:
        "Holiday cooking adds 4–8 cu ft of staging volume on top of normal groceries. Clearing space ahead of time prevents the day-before scramble where leftover containers compete with raw ingredients.",
    },
  ],
  relatedTools: ["food-storage-calculator", "refrigerator-size-calculator", "energy-cost-calculator"],
}

export function HolidayStorageCalculatorPageClient() {
  const [guests, setGuests] = useState("12")
  const [holiday, setHoliday] = useState("thanksgiving")
  const [hasTurkey, setHasTurkey] = useState(true)
  const [hasDesserts, setHasDesserts] = useState(true)
  const [hasSides, setHasSides] = useState(true)

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numGuests = readCount(guests, { min: 1, max: 100 })
    if (numGuests === null) return null

    const items: StorageItem[] = []
    let fridgeSpace = 0
    let freezerSpace = 0

    if (holiday === "thanksgiving" || holiday === "christmas") {
      if (hasTurkey) {
        const turkeyWeight = numGuests * 1.25
        const turkeyCuFt = turkeyWeight / 35
        items.push({
          item: `Turkey (${turkeyWeight.toLocaleString("en-US", { maximumFractionDigits: 0 })} lbs)`,
          cubicFeet: turkeyCuFt,
          tips: "USDA: thaw in fridge 24 hrs per 4–5 lbs.",
        })
        freezerSpace += turkeyCuFt
      }
      if (hasSides) {
        items.push({ item: "Mashed potatoes (prepared)", cubicFeet: 0.3, tips: "Make 1 day ahead, reheat before serving" })
        items.push({ item: "Stuffing / dressing", cubicFeet: 0.2, tips: "Prep ingredients 2 days ahead" })
        items.push({ item: "Cranberry sauce", cubicFeet: 0.1, tips: "Make up to 1 week ahead" })
        items.push({ item: "Green bean casserole", cubicFeet: 0.2, tips: "Assemble 1 day ahead, bake day-of" })
        items.push({ item: "Sweet potato casserole", cubicFeet: 0.2, tips: "Prep 2 days ahead" })
        fridgeSpace += 1.0
      }
      if (hasDesserts) {
        items.push({ item: "Pies (2–3)", cubicFeet: 0.4, tips: "Bake 1–2 days ahead" })
        items.push({ item: "Whipped cream", cubicFeet: 0.1, tips: "Make day-of for best texture" })
        fridgeSpace += 0.5
      }
      items.push({ item: "Beverages", cubicFeet: 0.5, tips: "Use cooler with ice for overflow" })
      items.push({ item: "Leftover containers (empty)", cubicFeet: 0.3, tips: "Have containers ready" })
      fridgeSpace += 0.8
    }
    if (holiday === "easter") {
      const hamWeight = numGuests * 0.5
      const hamCuFt = hamWeight / 40
      items.push({ item: `Ham (${hamWeight.toLocaleString("en-US", { maximumFractionDigits: 0 })} lbs)`, cubicFeet: hamCuFt, tips: "USDA: 0.5 lb / person bone-in." })
      fridgeSpace += hamCuFt
      items.push({ item: "Deviled eggs", cubicFeet: 0.2, tips: "Make 1 day ahead" })
      items.push({ item: "Side dishes", cubicFeet: 0.6, tips: "Prep 1–2 days ahead" })
      fridgeSpace += 0.8
    }
    if (holiday === "4th-july") {
      items.push({ item: "Burgers & hot dogs", cubicFeet: 0.4, tips: "Keep frozen until day before" })
      items.push({ item: "Potato salad", cubicFeet: 0.3, tips: "Make 1 day ahead" })
      items.push({ item: "Watermelon", cubicFeet: 0.5, tips: "Cut day-of, store in fridge" })
      fridgeSpace += 1.2
    }

    const totalCubicFeet = parseFloat((fridgeSpace + freezerSpace).toFixed(1))
    return {
      totalCubicFeet,
      fridgeSpace: parseFloat(fridgeSpace.toFixed(1)),
      freezerSpace: parseFloat(freezerSpace.toFixed(1)),
      items,
    }
  }, [guests, holiday, hasTurkey, hasDesserts, hasSides])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Will it all"
      heroTitleItalic="actually"
      heroTitlePost="fit?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~25 sec", "USDA portion guidance"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "1.25", label: "Lbs turkey / guest", note: "USDA" },
            { value: "0.5", label: "Lb ham / guest", note: "Bone-in" },
            { value: "24h", label: "Per 4–5 lb thaw", note: "Refrigerator" },
            { value: "3–4d", label: "Leftover safety", note: "@ 40°F" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "holiday-food-storage-calculator")}
      cta={{
        title: "If your fridge can't hold the holiday — upgrade.",
        body: "French door fridges with deli drawers, gallon door bins, and dedicated produce zones make 12-person dinners genuinely feasible.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Pick the holiday.
                <br />
                Pick the menu.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We map portions to refrigerator and freezer cubic-feet using USDA per-guest guidance.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Guests
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    step="1"
                    value={guests}
                    onChange={(e) => setGuests(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Holiday
                  </label>
                  <select
                    value={holiday}
                    onChange={(e) => setHoliday(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="thanksgiving">Thanksgiving</option>
                    <option value="christmas">Christmas</option>
                    <option value="easter">Easter</option>
                    <option value="4th-july">4th of July</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-[13px] font-bold cursor-pointer">
                    <input type="checkbox" checked={hasTurkey} onChange={(e) => setHasTurkey(e.target.checked)} className="w-4 h-4" />
                    Turkey
                  </label>
                  <label className="flex items-center gap-2 text-[13px] font-bold cursor-pointer">
                    <input type="checkbox" checked={hasSides} onChange={(e) => setHasSides(e.target.checked)} className="w-4 h-4" />
                    Sides
                  </label>
                  <label className="flex items-center gap-2 text-[13px] font-bold cursor-pointer">
                    <input type="checkbox" checked={hasDesserts} onChange={(e) => setHasDesserts(e.target.checked)} className="w-4 h-4" />
                    Desserts
                  </label>
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" /> Turkey checkbox only applies to Thanksgiving / Christmas.
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you change inputs
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Total holiday storage
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.totalCubicFeet ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">cu ft</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Fridge</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.fridgeSpace ?? "—"} cu ft</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Freezer</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.freezerSpace ?? "—"} cu ft</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Items breakdown */}
          {result && result.items.length > 0 && (
            <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                  Per-dish storage plan
                </h3>
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                  Cubic feet
                </span>
              </div>
              <div className="space-y-2">
                {result.items.map((row) => (
                  <div key={row.item} className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                    <div className="flex-1 pr-4">
                      <div className="text-[14px] font-semibold text-gray-900">{row.item}</div>
                      <div className="text-[12px] text-gray-500">{row.tips}</div>
                    </div>
                    <span className="text-[14px] font-mono font-bold text-[#0469f0]">
                      {row.cubicFeet.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cu ft
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · The right host fridge"
          title="French door fridges that swallow holiday menus"
          description="22–28 cu ft units with deli drawers, gallon door bins, and dedicated produce zones."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">USDA</em> portions. Real volumes.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Per-guest portion sizes are USDA Food Safety guidance. Volume estimates use density assumptions of ~35 lb/cu ft for poultry and 40 lb/cu ft for ham.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Per-guest meat math",
                  d: "Turkey: 1.25 lb × guests, divided by 35 lb/cu ft. Ham: 0.5 lb × guests, divided by 40 lb/cu ft.",
                },
                {
                  n: "02",
                  t: "Side & dessert volumes",
                  d: "Side dishes pre-allocated to typical 9×13 pan footprints (≈0.2 cu ft each). Pies allocated 0.4 cu ft for 2–3 standard pies.",
                },
                {
                  n: "03",
                  t: "Holiday timeline",
                  d: "Allocates space for thawing (3–4 days for turkey), make-ahead sides (1–2 days), and leftover containers (post-meal).",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Upright freezers for overflow holiday storage"
          description="Convert turkey-thaw chaos into calm — a dedicated upright freezer holds the whole bird and leftovers with room to spare."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
